import { request } from '@/helpers/apiClient';
import { useQuery } from '@tanstack/react-query';
import { HTTP_METHODS } from '@/utils/constants/httpMethods';
import { FeatureConfig } from '@/utils/constants/featuresFlag';
import { convertValuesInObject } from '@/helpers/convertValuesInObject';
import { allStores } from '@/mock/mockStores';
/**
 * API configuration for store-related endpoints
 */

// Utility function to handle pagination and filtering
const getPaginatedAndFilteredStores = (
  stores,
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  // Apply filters if any
  let filteredStores = stores;

  // Handle sorting first if present
  if (filters.sort) {
    const [sortKey, sortDirection] = filters.sort.split(':');
    filteredStores = [...filteredStores].sort((a, b) => {
      const aValue = String(a[sortKey] || '').toLowerCase();
      const bValue = String(b[sortKey] || '').toLowerCase();

      if (sortDirection === 'asc') {
        return bValue.localeCompare(aValue);
      } else {
        return aValue.localeCompare(bValue);
      }
    });
  }

  // Apply other filters
  if (Object.keys(filters).length > 0) {
    filteredStores = filteredStores.filter(store => {
      return Object.entries(filters).every(([key, value]) => {
        if (key === 'sort') return true; // Skip the sort filter
        if (!value) return true;
        const storeValue = store[key];
        return String(storeValue)
          .toLowerCase()
          .includes(String(value).toLowerCase());
      });
    });
  }

  // Apply date filters
  if (startDate && endDate) {
    filteredStores = filteredStores.filter(store => {
      const storeDate = new Date(store.createdDate);
      return storeDate >= startDate && storeDate <= endDate;
    });
  }

  // Calculate pagination
  const totalItems = filteredStores.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;

  // Get paginated results
  const items = filteredStores.slice(startIndex, endIndex);

  return {
    items,
    page,
    pageSize,
    totalItems,
    totalPages
  };
};

// Utility function to simulate delay
const simulateDelay = async () => {
  return new Promise(resolve => setTimeout(resolve, 1000));
};

export const fetchStoreListWithMissingProductList = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  await simulateDelay();
  // Filter stores that have empty or missing product lists
  const storesWithMissingProductList = allStores.filter(
    store =>
      !store.productList ||
      (Array.isArray(store.productList) && store.productList.length === 0)
  );

  return getPaginatedAndFilteredStores(
    storesWithMissingProductList,
    page,
    pageSize,
    filters,
    startDate,
    endDate
  );
};

export const fetchStoreListWithMissingVisitList = async (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  await simulateDelay();
  // Filter stores that have empty visit lists (missing deliveryDays array or empty array OR missing cutoffTime OR empty string)
  const storesWithMissingVisitList = allStores.filter(
    store =>
      !store.deliveryDays ||
      (Array.isArray(store.deliveryDays) && store.deliveryDays.length === 0)
  );

  return getPaginatedAndFilteredStores(
    storesWithMissingVisitList,
    page,
    pageSize,
    filters
  );
};

export const fetchStoreListWithMissingMOA = async (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  await simulateDelay();
  // Filter stores that have missing MOA
  const storesWithMissingMOA = allStores.filter(
    store => !store.minimumOrderAmount || store.minimumOrderAmount === null
  );

  console.log('storesWithMissingMOA', storesWithMissingMOA);

  return getPaginatedAndFilteredStores(
    storesWithMissingMOA,
    page,
    pageSize,
    filters
  );
};

export const fetchStoreListWithMissingCutOffTime = async (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  await simulateDelay();
  // Filter stores that have missing Cut Off Time
  const storesWithMissingCutOff = allStores.filter(
    store => !store.cutoffTime || store.cutoffTime === ''
  );

  return getPaginatedAndFilteredStores(
    storesWithMissingCutOff,
    page,
    pageSize,
    filters
  );
};

export const fetchStoreListWithMissingDeliveryService = async (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  await simulateDelay();
  // Filter stores that have missing Delivery Service
  const storesWithMissingDelivery = allStores.filter(
    store => !store.deliveryService || store.deliveryService === ''
  );

  return getPaginatedAndFilteredStores(
    storesWithMissingDelivery,
    page,
    pageSize,
    filters
  );
};

/**
 * Fetches store data by ID from the CEP API
 * @param {number} storeId - Store ID
 * @returns {Promise} - Promise with store data
 */
export const fetchStoreById = async storeId => {
  await simulateDelay();
  // fetch from mock using the params
  const filteredStores = allStores.filter(store => store.storeId === storeId);
  return filteredStores[0];
};

/**
 * Fetches paginated store list from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreList = async (page = 0, pageSize = 10, filters = {}) => {
  await simulateDelay();
  // fetch from mock using the params
  const filteredStores = allStores.filter(store => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      const storeValue = store[key];
      return String(storeValue)
        .toLowerCase()
        .includes(String(value).toLowerCase());
    });
  });

  // return the filtered stores
  return filteredStores;
};

/**
 * React Query hook for retrieving and caching store data by ID
 */
export const useStoreData = storeId => {
  return useQuery({
    queryKey: ['storeData', storeId],
    queryFn: () => fetchStoreById(storeId),
    enabled: !!storeId
  });
};

/**
 * React Query hook for retrieving and caching paginated store list
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreList = (page = 0, pageSize = 10, filters = {}) => {
  return useQuery({
    queryKey: ['storeList', page, pageSize, filters],
    queryFn: () => fetchStoreList(page, pageSize, filters)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with empty products list
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingProductList = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return useQuery({
    queryKey: [
      'storeListWithMissingProductList',
      page,
      pageSize,
      filters,
      startDate,
      endDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingProductList(
        page,
        pageSize,
        filters,
        startDate,
        endDate
      )
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with empty visit lists
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingVisitList = (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  return useQuery({
    queryKey: ['storeListWithMissingVisitList', page, pageSize, filters],
    queryFn: () => fetchStoreListWithMissingVisitList(page, pageSize, filters)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing MOA
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingMOA = (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  return useQuery({
    queryKey: ['storeListWithMissingMOA', page, pageSize, filters],
    queryFn: () => fetchStoreListWithMissingMOA(page, pageSize, filters)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing Cut Off Time
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingCutOffTime = (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  return useQuery({
    queryKey: ['storeListWithMissingCutOffTime', page, pageSize, filters],
    queryFn: () => fetchStoreListWithMissingCutOffTime(page, pageSize, filters)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing Delivery Service
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingDeliveryService = (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  return useQuery({
    queryKey: ['storeListWithMissingDeliveryService', page, pageSize, filters],
    queryFn: () =>
      fetchStoreListWithMissingDeliveryService(page, pageSize, filters)
  });
};
